<template>
  <div v-if="componentLoaded" class="w-full block">
    <!-- MANAGER EDITING BUTTON OPTIONS -->
    <div
      v-if="currentPost.id"
      class="w-full flex space-x-2 justify-start lg:justify-between -mt-8 py-4"
    >
      <img
        class="rounded-xl w-14 h-14 border-white border-2 transition-all flex-shrink-0"
        src="/img/logo-cw.png"
        alt="CareerWise logo"
      />

      <div class="w-full flex space-x-2 justify-start lg:justify-end">
        <button
          type="submit"
          class="btn btn-black"
          @click="showDeleteModal = !showDeleteModal"
        >
          <span>Delete</span>
        </button>

        <button
          :disabled="!canSubmit || thinking || thinkingDelete"
          :class="[
            !canSubmit && !thinking
              ? 'opacity-25 border border-truegray-700'
              : 'btn-cw-alt',
            thinking ? 'thinking-primary btn-cw-alt cursor-not-allowed' : '',
          ]"
          type="submit"
          class="btn active:bg-cw-alt relative"
          @click="submitUpdates"
        >
          <div
            class="absolute inset-0 w-full flex justify-center items-center z-20"
          >
            <lottie v-if="thinking" :options="loadingAnimationOptions" />
          </div>
          <span :class="[thinking ? 'invisible' : '']">Save post</span>
        </button>
      </div>
    </div>

    <div
      v-if="showDeleteModal"
      id="showModal"
      class="fixed z-70 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >

        <!-- Modal panel, show/hide based on modal state. -->
        <div
          :class="[
            showDeleteModalContents
              ? 'translate-y-0'
              : 'translate-y-full opacity-0',
          ]"
          class="bg-white transform delay-100 w-full sm:max-w-3xl sm:w-full inline-block rounded-lg p-12 sm:mt-8 sm:mb-20 sm:p-16 text-left shadow transition-all align-bottom sm:align-middle overflow-visible relative"
        >
          <h2 class="w-full mb-4 text-center"><span>Delete Post</span></h2>

          <p class="mb-4 text-md text-center">
            Do you want to delete this post forever?
          </p>

          <button
            :disabled="thinking || thinkingDelete"
            type="submit"
            class="btn btn-cw-alt m-auto mt-10 relative"
            @click="deletePost"
          >
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie
                v-if="thinkingDelete"
                :options="loadingAnimationOptions"
              />
            </div>
            <span :class="[thinkingDelete ? 'invisible' : '']">Confirm</span>
          </button>

          <button
            type="button"
            class="absolute right-8 top-6 transform hover:rotate-180 transition-all duration-300 hover:text-cw-alt"
            @click="showDeleteModal = !showDeleteModal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="w-full h-full flex flex-wrap lg:flex-nowrap">
      <!-- Left Col -->
      <div
        class="w-full lg:w-4/12 pr-4 pb-8 lg:pb-0 mb-8 lg:mb-0 border-b lg:border-b-0 border-truegray-300 transition-all duration-300 flex flex-col space-y-4"
      >
        <p class="uppercase text-sm font-semibold mb-2">Post category</p>

        <!-- Category -->
        <InputDropdown
          v-if="data_loaded"
          :label="'Category (required)'"
          :selected-options-initial="[post_category]"
          :options="post_category_types"
          :placeholder-default="categoryPlaceholder"
          :response-selection-id="'update_post_category'"
        />

        <!-- Publish/Draft Toggle -->
        <div class="w-full flex justify-start items-center space-x-3">
          <button
            type="button"
            :class="[publish_status ? 'bg-cw-alt' : 'bg-gray-200', toggleClass]"
            role="switch"
            aria-checked="false"
            @click="publish_status = !publish_status"
          >
            <span
              aria-hidden="true"
              class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-all ring-0 ease-in-out duration-200"
              :class="[publish_status ? 'translate-x-5' : 'translate-x-0']"
            ></span>
          </button>
          <span v-if="publish_status" class="mb-8">Publish</span>
          <span v-else class="mb-8">Draft</span>
        </div>

        <!-- EVENT CATEGORY SELECTED -->
        <div v-if="post_category === 3" class="w-full dj-datepicker">
          <!-- Date Picker -->
          <p class="uppercase text-sm font-semibold mb-2">Event fields</p>
          <div class="w-full relative mb-4">
            <h6 class="text-truegray-700">Event Date(s) (required)</h6>
            <date-picker
              v-model="post_event_date_range"
              range
              value="datetime"
              value-type="format"
              format="DD-MM-YYYY"
            ></date-picker>
          </div>
          <div class="w-full relative mb-8">
            <h6 class="text-truegray-700">Start Time (optional)</h6>
            <date-picker
              v-model="post_event_start_at_time"
              format="hh:mm a"
              value-type="format"
              type="time"
              :minute-step="15"
            ></date-picker>
          </div>

          <!-- Event Venue Title -->
          <div :class="inputWrapperClass" class="h-12">
            <h6 :class="inputLabelClass">Venue Name (required)</h6>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 absolute right-2 bottom-1/2 transform translate-y-1/2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <input
              id="event_venue_title"
              v-model="post_event_venue_title"
              type="text"
              name="event_venue_title"
              autocomplete="event_venue_title"
              :class="inputTextClass"
            />
          </div>

          <!-- Event Venue Address -->
          <div :class="inputWrapperClass" class="h-12">
            <h6 :class="inputLabelClass">Venue Address (optional)</h6>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 absolute right-2 bottom-1/2 transform translate-y-1/2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
            <input
              id="event_venue_address"
              v-model="post_event_venue_address"
              type="text"
              name="event_venue_address"
              autocomplete="event_venue_address"
              :class="inputTextClass"
              class="mr-4"
            />
          </div>
        </div>

        <!-- JOB -->
        <div v-if="post_category === 2">
          <p class="uppercase text-sm font-semibold mb-2">Job fields</p>

          <!-- Job type -->
          <div :class="inputWrapperClass" class="h-12">
            <!-- Label -->
            <h6 :class="inputLabelClass">Job Type (required)</h6>
            <!-- Select -->
            <button
              :class="ddButtonClass"
              @click="ddJobTypeActive = !ddJobTypeActive"
            >
              <span
                v-if="post_job_type === 0 || post_job_type === 1"
                :class="[
                  ddSpanClass,
                  !ddJobTypeActive ? 'text-black' : 'text-truegray-300',
                ]"
              >
                {{ post_job_types[post_job_type].title }}
              </span>
            </button>
            <!-- Icons -->
            <svg
              v-if="!ddJobTypeActive"
              :class="ddSvgClass"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              @click="ddJobTypeActive = true"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              :class="ddSvgClass"
              viewBox="0 0 20 20"
              fill="currentColor"
              @click="ddJobTypeActive = false"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
            <!-- Options -->
            <select
              id="post_job_type"
              v-model="post_job_type"
              name="post_job_type"
              autocomplete="post_job_type"
              class="hidden"
            >
              <option :value="0">Part-time</option>
              <option :value="1">Full-time</option>
              <option :value="2">Casual</option>
            </select>
            <ul
              :class="[
                ddUlClass,
                ddJobTypeActive
                  ? 'top-12 opacity-1'
                  : 'top-0 opacity-0 invisible',
              ]"
            >
              <li
                v-for="(option, i) in post_job_types"
                :key="i"
                :class="ddLiClass"
                @click="updateJobType(option.value)"
              >
                {{ option.title }}
              </li>
            </ul>
          </div>

          <!-- Job Employer -->
          <div :class="inputWrapperClass" class="h-12">
            <h6 :class="inputLabelClass">Employer (required)</h6>
            <input
              id="post_job_employer"
              v-model="post_job_employer"
              type="text"
              name="post_job_employer"
              autocomplete="post_job_employer"
              :class="inputTextClass"
            />
          </div>
        </div>

        <p class="uppercase text-sm font-semibold mb-2">General fields</p>

        <!-- Post Title -->
        <div :class="inputWrapperClass" class="h-12">
          <h6 :class="inputLabelClass">Title (required)</h6>
          <input
            id="title"
            v-model="post_title"
            type="text"
            name="title"
            autocomplete="title"
            :class="inputTextClass"
          />
        </div>

        <!-- General tags -->
        <InputDropdown
          v-if="data_loaded"
          :label="'General tags (1 tag type required)'"
          :selected-options-initial="general_tags_selected"
          :options="general_tags"
          :margin-bottom="false"
          :border-enabled="true"
          :multiselect="true"
          :response-selection-id="'update_general_tags'"
        />

        <!-- Industry tags -->
        <InputDropdown
          v-if="data_loaded && post_category !== 0"
          :label="'Industry tags (1 tag type required)'"
          :selected-options-initial="industry_tags_selected"
          :options="industry_tags"
          :margin-bottom="false"
          :multiselect="true"
          :response-selection-id="'update_industry_tags'"
        />

        <!-- Year tags -->
        <InputDropdown
          v-if="data_loaded"
          :label="'Year groups (optional)'"
          :selected-options-initial="year_tags_selected"
          :options="year_tags"
          :margin-bottom="false"
          :border-enabled="true"
          :multiselect="true"
          :response-selection-id="'update_year_tags'"
        />

        <!-- Post Link title and URL -->
        <div :class="inputWrapperClass" class="h-12">
          <h6 :class="inputLabelClass" class="flex items-center group">
            Link title (optional)
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-3 w-3 ml-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
            <div :class="infoClass">
              Creates a button linking to an external resource or website
            </div>
          </h6>
          <input
            id="post_link_title"
            v-model="post_link_title"
            type="text"
            name="post_link_title"
            autocomplete="post_link_title"
            :class="inputTextClass"
          />
        </div>
        <div :class="inputWrapperClass" class="h-12 -mt-4">
          <h6 :class="inputLabelClass">Link URL (optional)</h6>
          <input
            id="post_link_url"
            v-model="post_link_url"
            type="text"
            name="post_link_url"
            autocomplete="post_link_url"
            :class="inputTextClass"
          />
          <p v-if="!valid_link_url" class="text-xs text-red-500">
            Please provide a valid URL
          </p>
        </div>

        <!-- Resources Ordering -->
        <div class="relative top-8 z-60">
          <p class="uppercase text-sm font-semibold mt-4 mb-2">
            Post Resources
          </p>

          <div v-show="post_resources.length < 6" class="flex items-center">
            <button
              type="button"
              class="btn btn-black m-0"
              @click="showResourcesSearchModal = !showResourcesSearchModal"
            >
              Add Resource
            </button>
          </div>

          <!-- Attached files -->
          <div v-show="post_resources.length > 0" class="my-8">
            <h6>Attached resources</h6>

            <vue-nestable v-model="post_resources" :max-depth="1">
              <vue-nestable-handle
                slot-scope="{ item }"
                :item="item"
                class="flex justify-start w-full space-x-2 bg-gray-50 p-2 rounded border my-1"
              >
                <div class="flex justify-start w-full space-x-4">
                  <button
                    v-if="post_resources.length > 1"
                    class="drag"
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 hover:text-cw-alt transition-all duration-100"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M3 7a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 13a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <span>{{ item.title }}</span>
                </div>

                <div class="group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-300 hover:text-gray-500 cursor-pointer transition-all duration-100"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    @click="detachResource(item.source_id)"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div
                    class="ml-2 text-xs absolute left-full top-0 w-24 z-50 border border-truegray-300 rounded py-2 px-4 opacity-0 transition-all duration-300 transform pointer-events-none group-hover:opacity-100 bg-white text-center"
                  >
                    Remove Resource
                  </div>
                </div>
              </vue-nestable-handle>
            </vue-nestable>
          </div>
        </div>
        <!-- /Files -->
      </div>
      <!-- /Left Col -->

      <!-- Right Col -->
      <div
        class="w-full lg:w-8/12 lg:border-l border-truegray-300 lg:pl-6 transition-all duration-300 ease-in-out relative"
      >
        <!-- Region Tag FOR CLOUD ONLY -->
        <div class="w-full max-w-xs">
          <InputDropdown
            v-if="data_loaded"
            :label="'Regions (required)'"
            :category="post_category"
            :selected-options-initial="[region_tag_selected]"
            :options="region_tags"
            :border-enabled="true"
            :margin-bottom="false"
            :placeholder-default="'Select a region'"
            :response-selection-id="'update_region_tags'"
          />
        </div>

        <!-- SHARE TO CLOUD SECTION -->

        <!-- Featured image -->
        <div class="py-4 w-full relative mr-0 lg:mr-2">
          <label
            for="featuredImage"
            class="block text-sm font-medium text-gray-700"
            >Featured image</label
          >
          <div class="mt-1 flex items-center">
            <div
              :class="imgWrapperClass"
              :style="{ backgroundImage: `url(${previewImage})` }"
            >
              <button
                v-if="!previewImageChanged"
                type="button"
                :class="imgButtonClass"
                @click="showImageOptionsModal = !showImageOptionsModal"
              >
                Change
              </button>
              <button
                v-else
                type="button"
                :class="imgButtonClass"
                @click="resetImage"
              >
                Cancel
              </button>
              <!-- @todo does website_title exist? -->
              <span class="hidden">{{ settings.website_title }}</span
              ><!-- Required ... spoof the dom -->
            </div>
            <form ref="selectImageForm">
              <input
                ref="selectImage"
                type="file"
                class="hidden"
                @input="pickImage"
              />
            </form>
          </div>
          <p class="text-xs text-truegray-500 mb-4">
            .jpg or .png format with a maximum file size of 1mb
          </p>
          <div v-if="previewImage" class="relative flex items-start">
            <div class="flex items-center h-5">
              <input
                id="post_image_featured_remove"
                v-model="post_image_featured_remove"
                name="post_image_featured_remove"
                type="checkbox"
                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 rounded"
              />
            </div>
            <div class="ml-3 text-sm">
              <label
                for="post_image_featured_remove"
                class="font-medium text-gray-700"
                >Remove featured image</label
              >
            </div>
          </div>
        </div>

        <!-- Description -->
        <div :class="inputWrapperClass">
          <h6 :class="inputLabelClass">Description (optional)</h6>
          <textarea
            id="description"
            v-model="post_description"
            name="description"
            rows="2"
            :class="inputTextAreaClass"
            @keydown="descriptionValidation"
            @change="descriptionValidation"
          ></textarea>
        </div>
        <div class="text-xs text-truegray-500 mb-4 -mt-6">
          <span v-if="!description_word_limit"
            >You are under the word limit</span
          >
          <span v-if="description_word_limit" class="text-red-500"
            >You have reached the word limit</span
          >
        </div>
        <!-- /Description -->

        <!-- Body -->
        <div class="mb-8">
          <h6>Post content (required)</h6>
          <Editor
            :key="post_body_original"
            :initial-content="post_body_original"
            :active-features="[
              'headings',
              'bold',
              'italic',
              'strike',
              'highlight',
              'text_colour',
              'underline',
              'h1',
              'h2',
              'h3',
              'alignments',
              'alignLeft',
              'alignCenter',
              'alignRight',
              'lists',
              'bulletList',
              'orderedList',
              'blockquote',
              'code',
              'codeBlock',
              'link',
              'remove_link',
              'image',
              'tables',
              'table',
              'horizontalRule',
              'undo',
              'redo',
            ]"
            @update-content="updateEditorContent"
          />
        </div>
        <!-- /Body -->
      </div>
      <!-- /Right Col -->

      <!-- Featured image modal -->
      <div
        v-if="showImageOptionsModal"
        id="showImageOptionsModal"
        class="fixed z-60 inset-0 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          ></div>
          <!-- This element is to trick the browser into centering the modal contents. -->
          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
            >&#8203;</span
          >
          <!-- Modal panel, show/hide based on modal state. -->
          <div
            :style="{ height: 'calc(100vh - 10rem)' }"
            :class="resources_available ? '' : 'bg-truegray-500'"
            class="bg-cover inline-block align-bottom bg-white border-8 border-white rounded-4xl text-left shadow transform transition-all duration-300 ease-in-out sm:my-8 sm:align-middle sm:max-w-4xl sm:max-h-lg sm:w-full relative overflow-scroll no-scrollbar"
          >
            <div class="flex justify-between px-6 lg:px-16 lg:pt-6">
              <h3 v-if="resources_available">Image Resources</h3>
              <button
                type="button"
                class="transform hover:rotate-180 transition-all duration-300 hover:text-cw-alt"
                @click="showImageOptionsModal = !showImageOptionsModal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div class="px-6 lg:px-16 lg:pt-6">
              <MediaItems
                :types="[2]"
                :post="true"
                :cloud="true"
                :image_files_only="true"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Resource Media Modal -->
      <div
        v-if="showResourcesSearchModal"
        id="showResourcesSearchModal"
        class="fixed z-60 inset-0 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          ></div>
          <!-- This element is to trick the browser into centering the modal contents. -->
          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
            >&#8203;</span
          >

          <!-- Modal panel, show/hide based on modal state. -->
          <div
            :style="{ height: 'calc(100vh - 10rem)' }"
            :class="resources_available ? '' : 'bg-truegray-500'"
            class="bg-cover inline-block align-bottom bg-white border-8 border-white rounded-4xl text-left shadow transform transition-all duration-300 ease-in-out sm:my-8 sm:align-middle sm:max-w-4xl sm:max-h-lg sm:w-full relative overflow-scroll no-scrollbar"
          >
            <div class="flex justify-between px-6 lg:px-16 lg:pt-6">
              <h3 v-if="resources_available">Resources</h3>
              <button
                type="button"
                class="transform hover:rotate-180 transition-all duration-300 hover:text-cw-alt"
                @click="showResourcesSearchModal = !showResourcesSearchModal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div class="px-6 lg:px-16 lg:pt-6">
              <MediaItems :cloud="true" :post="true" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '../../../js/plugins/event-bus.js'
import { shared } from '../../../js/mixins/shared.js'
import InputDropdown from '../../../shared/components/InputDropdown.vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import MediaItems from '../../../shared/tenant/MediaItems.vue'
import { VueNestable, VueNestableHandle } from 'vue-nestable'
import Editor from '../../../careerwise/tenant/manager/components/editor-wysiwyg/Editor.vue'
import * as loadingAnimation from '../../../shared/components/loading.json'
import { mapActions, mapGetters } from 'vuex'
import { checkPositiveInteger } from '../../../js/util/helper.js'

export default {
  components: {
    DatePicker,
    InputDropdown,
    VueNestable,
    VueNestableHandle,
    MediaItems,
    Editor,
  },

  mixins: [shared],

  props: {
    post_id: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  data() {
    return {
      componentLoaded: false,
      superAdmin: false,

      debug: false,
      resources_available: false,

      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      thinking: false,
      thinkingDelete: false,
      thinkingResourceSearch: false,

      post_category_types: [
        { text: 'News', value: 1 },
        { text: 'Event', value: 3 },
        { text: 'Job', value: 2 },
      ],

      post_job_types: [
        { title: 'Part-time', value: 0 },
        { title: 'Full-time', value: 1 },
        { title: 'Casual', value: 2 },
      ],

      //Datepicker
      showTimePanel: false,
      showTimeRangePanel: false,

      // dropdowns active
      ddCategoryActive: false,
      ddJobTypeActive: false,

      //input styles
      toggleClass:
        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-all ease-in-out duration-200 focus:outline-none mb-8',
      inputWrapperClass:
        'rounded relative bg-white shadow border border-truegray-300 mb-8 ',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-truegray-700',
      inputTextClass:
        'flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300',
      inputTextAreaClass:
        'flex-grow px-4 mr-0 mt-5 outline-none focus:outline-none bg-transparent h-24 font-primary text-black  focus:placeholder-truegray-300 transition-all duration-100 ease-in-out w-full',
      ddButtonClass:
        'w-full flex-grow px-4 mr-0 outline-none focus:outline-none bg-transparent h-12 font-primary text-black cursor-pointer',
      ddSvgClass: 'cursor-pointer z-10 h-12 w-5 absolute top-0 flex right-2',
      ddSpanClass: 'block text-base font-primary truncate text-left mt-2',
      ddUlClass:
        'no-scrollbar transform ease-in-out duration-300 max-h-56 shadow-md px-6 w-full absolute left-0 z-50 bg-white text-base sm:text-sm overflow-auto focus:outline-none rounded-b-xl',
      ddLiClass:
        'w-full cursor-pointer border-b last:border-transparent relative flex items-center px-2 h-12',
      imgWrapperClass:
        'banner-changer w-full h-72 my-2 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20',
      imgButtonClass:
        'z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6',
      infoClass:
        'absolute left-full top-0 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white',

      post_category_name: ['News', 'Job', 'Event'],

      canSubmit: false,

      resourcesLoaded: false,
      post_resources: [],
      post_resources_original: [],
      resourceListOrder: [],
      showResourcesSearchModal: false,

      search_resources: '',
      submit_keywords_button_disabled: true,
      resource_search_list: false,
      resource_search: [],

      // Pagination
      resource_pagination: {
        current_page: null,
        total_pages: null,
        per_page: 9,
        next_page: null,
        previous_page: null,
        last_page_url: null,
        next_page_url: null,
        prev_page_url: null,
        path: null,
        nextpage: null,
        previouspage: null,
      },

      post_status: null,
      post_status_original: null,
      publish_status: null,

      post_category: null,
      post_category_original: null,

      post_title: '',
      post_title_original: '',

      post_description: '',
      post_description_clear: false,
      post_description_original: '',
      description_word_limit: false,

      post_body: '',
      post_body_original: '',

      // TAGS:
      general_tags: [],
      general_tags_selected: [],
      general_tags_selected_original: [],

      industry_tags: [],
      industry_tags_selected: [],
      industry_tags_selected_original: [],

      year_tags: [],
      year_tags_selected: [],
      year_tags_selected_original: [],

      region_tags: [],
      region_tag_selected: null,
      region_tag_selected_original: null,

      all_tags_selected: [],

      data_loaded: false,

      post_link_title: '',
      post_link_title_original: '',

      post_link_url: '',
      post_link_url_original: '',
      valid_link_url: true,

      cloud_post: true,
      cloud_post_original: null,

      // Featured image:
      showImageOptionsModal: false,

      post_image_featured: null,
      post_image_featured_original: null,
      previewImage: null,
      preview_image_resource_id: null,
      previewImageChanged: false,
      post_image_featured_remove: false,

      post_event_date_range: null,
      post_event_date_range_original: null,
      post_event_start_at_time: null,
      post_event_start_at_time_original: null,
      post_event_venue_title: null,
      post_event_venue_title_original: null,
      post_event_venue_address: null,
      post_event_venue_address_original: null,

      post_job_type: null,
      post_job_type_original: null,
      post_job_employer: null,
      post_job_employer_original: null,

      mandatory_options: ['title', 'category', 'region_tag_selected'],

      update: [],

      showDeleteModal: false,
      showDeleteModalContents: false,
    }
  },

  computed: {
    // Set all map getters
    ...mapGetters({
      settings: 'shared/getSettings',
      userAccess: 'shared/getUserAccess',
      currentPost: 'shared/getCurrentPost',
    }),

    categoryPlaceholder() {
      return (
        this.post_category_types.find((opt) => opt.value === this.post_category)
          ?.text || ''
      )
    },
  },

  watch: {
    showDeleteModal: {
      handler: function (active) {
        EventBus.$emit('modal_state_changing', active)
        if (!active) {
          this.$set(this, 'showDeleteModalContents', false)
        } else {
          setTimeout(() => {
            this.$set(this, 'showDeleteModalContents', true)
          }, 100)
        }
      },

      deep: true,
    },

    currentPost: {
      handler: function () {
        this.getAndSetCurrentPostData('currentPost')
      },

      deep: true,
    },

    general_tags_selected: {
      handler: function () {
        this.setAllTags()
      },

      deep: true,
    },

    industry_tags_selected: {
      handler: function () {
        this.setAllTags()
      },

      deep: true,
    },

    year_tags_selected: {
      handler: function () {
        this.setAllTags()
      },

      deep: true,
    },

    region_tag_selected: {
      handler: function () {
        this.setAllTags()
      },

      deep: true,
    },

    // Resource watches
    post_resources: {
      handler: function (file_list) {
        let originalList = []
        for (let i = 0; i < this.post_resources_original.length; i++) {
          originalList.push(this.post_resources_original[i].id)
        }

        let newList = []
        for (let j = 0; j < file_list.length; j++) {
          newList.push(file_list[j].id)
        }

        let thesame = this.isEquivalent(originalList, newList)

        if (thesame) {
          for (let k = 0; k < this.update.length; k++) {
            if (this.update[k] === 'resources_order') {
              this.update.splice(k, 1)
            }
          }
        } else {
          if (!this.update.includes('resources_order')) {
            this.update.push('resources_order')
          }
        }

        this.processUpdate()
      },

      deep: true,
    },

    search_resources: {
      handler: function (keywords) {
        if (keywords.length > 0) {
          this.$set(this, 'submit_keywords_button_disabled', false)
        } else {
          this.$set(this, 'submit_keywords_button_disabled', true)
        }
      },

      deep: true,
    },

    publish_status: {
      // Toggle option effecing another data point
      handler: function (val) {
        val = val ? 1 : 0
        this.$set(this, 'post_status', val)
      },

      deep: true,
    },

    post_status: {
      handler: function (val) {
        if (val === this.post_status_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'status') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('status')) {
            this.update.push('status')
          }
        }

        this.processUpdate()
      },

      deep: true,
    },

    post_category: {
      handler: function (val) {
        // IF EVENT ?
        let mandatory_options = this.mandatory_options
        if (Math.abs(val) === 3) {
          // Should be integer - make both test condition ABS number

          if (this.mandatory_options.indexOf('event_date_range') < 1) {
            this.mandatory_options.push('event_date_range')
          }
          if (this.mandatory_options.indexOf('event_venue_title') < 1) {
            this.mandatory_options.push('event_venue_title')
          }
        } else {
          for (let i = 0; i < mandatory_options.length; i++) {
            if (mandatory_options[i] === 'event_date_range') {
              mandatory_options.splice(i, 1)
            }
            if (mandatory_options[i] === 'event_venue_title') {
              mandatory_options.splice(i, 1)
            }
            if (mandatory_options[i] === 'event_venue_title') {
              mandatory_options.splice(i, 1)
            }
          }

          // Reset event_start_at and event_end_at
          this.$set(this, 'post_event_date_range', null)
          this.$set(this, 'post_event_start_at_time', null)
          this.$set(this, 'post_event_venue_title', null)
        }

        // IF JOB ?
        if (Math.abs(val) === 2) {
          // Should be integer - make both test condition ABS number
          if (this.mandatory_options.indexOf('job_type') < 1) {
            this.mandatory_options.push('job_type')
          }
          if (this.mandatory_options.indexOf('job_employer') < 1) {
            this.mandatory_options.push('job_employer')
          }
        } else {
          for (let j = 0; j < mandatory_options.length; j++) {
            if (mandatory_options[j] === 'job_type') {
              mandatory_options.splice(j, 1)
            }
            if (mandatory_options[j] === 'job_employer') {
              mandatory_options.splice(j, 1)
            }
          }

          // Reset event_start_at and event_end_at
          this.$set(this, 'post_job_type', null)
          this.$set(this, 'post_job_employer', null)
        }

        this.$set(this, 'mandatory_options', mandatory_options)

        if (Math.abs(this.post_category_original) === Math.abs(val)) {
          for (let k = 0; k < this.update.length; k++) {
            if (this.update[k] === 'category') {
              this.update.splice(k, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('category')) {
            this.update.push('category')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_event_date_range: {
      // String option
      handler: function (val) {
        if (val === this.post_event_date_range_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'event_date_range') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('event_date_range')) {
            this.update.push('event_date_range')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_event_start_at_time: {
      // String option
      handler: function (val) {
        if (val === this.post_event_start_at_time_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'event_start_at_time') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('event_start_at_time')) {
            this.update.push('event_start_at_time')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_event_venue_title: {
      // String option
      handler: function (val) {
        if (val === this.post_event_venue_title_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'event_venue_title') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('event_venue_title')) {
            this.update.push('event_venue_title')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_event_venue_address: {
      // String option
      handler: function (val) {
        if (val === this.post_event_venue_address_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'event_venue_address') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('event_venue_address')) {
            this.update.push('event_venue_address')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_job_type: {
      handler: function (val) {
        if (val === this.post_job_type_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'job_type') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('job_type')) {
            this.update.push('job_type')
          }
        }
      },

      deep: true,
    },

    post_job_employer: {
      handler: function (val) {
        if (val === this.post_job_employer_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'job_employer') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('job_employer')) {
            this.update.push('job_employer')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_title: {
      // String option
      handler: function (val) {
        if (val === this.post_title_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'title') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('title')) {
            this.update.push('title')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_description: {
      // String option
      handler: function (val) {
        if (val === this.post_description_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'description') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('description')) {
            this.update.push('description')
          }
        }

        this.processUpdate()
      },

      deep: true,
    },

    post_link_title: {
      // String option
      handler: function (val) {
        if (val === this.post_link_title_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'link_title') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('link_title')) {
            this.update.push('link_title')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_link_url: {
      // String option
      handler: function (url) {
        if (!url) {
          this.$set(this, 'post_link_url', '')
          return
        }

        if (url.length > 0) {
          if (!this.validateURL(url)) {
            this.$set(this, 'valid_link_url', false)
          } else {
            if (!this.validateURLProtocol(url)) {
              url = this.$set(
                this,
                'post_link_url',
                this.updateURLWithProtocol(url),
              )
            }
            this.$set(this, 'valid_link_url', true)
          }
        } else {
          this.$set(this, 'valid_link_url', true)
        }

        if (url === this.post_link_url_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'link_url') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('link_url')) {
            this.update.push('link_url')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_body: {
      // String option
      handler: function (val) {
        if (val === this.post_body_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'body') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('body')) {
            this.update.push('body')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    showResourcesSearchModal: {
      handler: function (val) {
        EventBus.$emit('modal_state_changing', val)
        if (!val) {
          this.$set(this, 'resources_available', false)
        }
      },

      deep: true,
    },

    showImageOptionsModal: {
      handler: function (val) {
        EventBus.$emit('modal_state_changing', val)
        if (!val) {
          this.$set(this, 'resources_available', false)
        }
      },

      deep: true,
    },

    previewImageChanged: {
      // PUT IN HERE INSTEAD
      handler: function (val) {
        if (!val) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'image_featured') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('image_featured')) {
            this.update.push('image_featured')
          }
        }
        this.processUpdate()
      },

      deep: true,
    },

    post_image_featured_remove: {
      // Checkbox option remove another data point
      handler: function (val) {
        if (!val) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'image_remove') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('image_remove')) {
            this.update.push('image_remove')
          }
        }

        this.processUpdate()
      },

      deep: true,
    },
  },

  async mounted() {
    await this.runUpdates()
    this.$set(this, 'componentLoaded', true)

    EventBus.$on('resources_ready', () => {
      this.$set(this, 'resources_available', true)
    })

    this.$set(this, 'general_tags', this.settings.tags)
    this.$set(this, 'year_tags', this.settings.tag_years)
    this.$set(this, 'region_tags', this.settings.tag_regions)
    this.$set(this, 'industry_tags', this.settings.tag_industries)

    if (this.currentPost) {
      this.getAndSetCurrentPostData(this.currentPost)
    }

    EventBus.$on('deleteResponseReturned', () => {
      this.thinkingDelete = false
    })

    EventBus.$on('update_post_category', (selected) => {
      this.$set(this, 'post_category', selected)
    })

    EventBus.$on('update_general_tags', (arr) => {
      this.$set(this, 'general_tags_selected', arr)
    })

    EventBus.$on('update_industry_tags', (sel) => {
      this.$set(this, 'industry_tag_selected', sel)
    })

    EventBus.$on('update_year_tags', (sel) => {
      this.$set(this, 'year_tags_selected', sel)
    })

    EventBus.$on('update_region_tags', (sel) => {
      this.$set(this, 'region_tag_selected', sel)
    })

    EventBus.$on('assignResource', (resource) => {
      this.assignResource(resource)
    })

    EventBus.$on('assignResourceImage', (resource) => {
      this.assignResourceImage(resource)
    })
  },

  methods: {
    ...mapActions({
      runUpdates: 'shared/runUpdates',
      dispatchSetCurrentPost: 'shared/dispatchSetCurrentPost',
    }),

    updateEditorContent(val) {
      this.$set(this, 'post_body', val)
    },

    setAllTags() {
      // Clear out all selected tags:
      this.$set(this, 'all_tags_selected', [])
      let selected = []
      if (checkPositiveInteger(this.region_tag_selected)) {
        selected.push(this.region_tag_selected)
      }
      this.general_tags_selected.forEach((i) => {
        selected.push(i)
      })
      this.industry_tags_selected.forEach((i) => {
        selected.push(i)
      })
      this.year_tags_selected.forEach((i) => {
        selected.push(i)
      })
      this.$set(this, 'all_tags_selected', selected)
      if (this.data_loaded) {
        this.validateTagUpdate(selected)
      }
    },

    descriptionValidation(event) {
      if (this.post_description.length >= 160) {
        this.$set(this, 'description_word_limit', true)
        if (event.keyCode >= 48 && event.keyCode <= 90) {
          event.preventDefault()
          return
        }
      } else {
        this.$set(this, 'description_word_limit', false)
      }
    },

    validateTagUpdate(current) {
      let update = false
      let original = this.currentPost.tag_ids
      if (original) {
        // Increment the score if the updated general_tags_selected arr does not have tag id in original store data
        current.forEach((i) => {
          if (!original.includes(i)) {
            update = true
          }
        })
        // Increment score if any id in original store data does not exist in the general_tags_selected arr
        original.forEach((i) => {
          if (!current.includes(i)) {
            update = true
          }
        })
        if (!update) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'tags') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('tags')) {
            this.update.push('tags')
          }
        }

        this.processUpdate()
      }
    },

    previewPost() {
      EventBus.$emit('post-toggle-editing')
      if (this.update) {
        this.update.forEach((i) => {
          let node = this.returnDataNodeValue(i)
          this.currentPost[i] = node
        })
      }
    },

    isEquivalent(a, b) {
      // Create arrays of property names
      let aProps = Object.getOwnPropertyNames(a)
      let bProps = Object.getOwnPropertyNames(b)

      // If number of properties is different,
      // objects are not equivalent
      if (aProps.length !== bProps.length) {
        return false
      }

      for (let i = 0; i < aProps.length; i++) {
        let propName = aProps[i]
        // If values of same property are not equal,
        // objects are not equivalent
        if (a[propName] !== b[propName]) {
          return false
        }
      }

      // If we made it this far, objects
      return true
    },

    editResource(id = 0) {
      this.$router.push({ path: '/manager/resource/' + id })
    },

    searchResources(gotopage = 0) {
      this.thinkingResourceSearch = true
      let submitData = {}
      submitData.search = { keywords: this.search_resources }

      // Pagination page?
      if (gotopage) {
        submitData.page = gotopage
      }

      // Get this post...
      axios.post('/api/auth/resources', submitData).then((response) => {
        this.thinkingResourceSearch = false
        let result = response.data
        if (result.success) {
          this.$set(this, 'resource_search', result.resources.data)
          this.$set(this, 'resource_search_list', true)

          this.$set(
            this.resource_pagination,
            'current_page',
            response.data.pagination.current_page,
          )
          this.$set(
            this.resource_pagination,
            'total_pages',
            response.data.pagination.total,
          )
          this.$set(
            this.resource_pagination,
            'per_page',
            response.data.pagination.per_page,
          )
          this.$set(
            this.resource_pagination,
            'last_page_url',
            response.data.pagination.last_page_url,
          )
          this.$set(
            this.resource_pagination,
            'next_page_url',
            response.data.pagination.next_page_url,
          )
          this.$set(
            this.resource_pagination,
            'prev_page_url',
            response.data.pagination.prev_page_url,
          )
          this.$set(
            this.resource_pagination,
            'path',
            response.data.pagination.next_page_path,
          )

          let prev = null
          if (this.resource_pagination.prev_page_url) {
            prev = this.resource_pagination.prev_page_url.split('=')
            prev = prev[1]
          }

          let next = null
          if (this.resource_pagination.next_page_url) {
            next = this.resource_pagination.next_page_url.split('=')
            next = next[1]
          }

          this.$set(this.resource_pagination, 'previouspage', prev)
          this.$set(this.resource_pagination, 'nextpage', next)
        } else {
          //...
        }
      })
    },

    detachResource(source_id = 0) {
      let arr = []
      for (let i = 0; i < this.post_resources.length; i++) {
        if (this.post_resources[i].source_id === source_id) {
          // Do nothing
        } else {
          arr.push(this.post_resources[i])
        }
      }

      this.$set(this, 'post_resources', [])
      this.$set(this, 'post_resources', arr)
    },

    assignResourceImage(resource) {
      this.$set(this, 'showImageOptionsModal', false)

      this.$set(this, 'previewImageChanged', true)
      this.$set(this, 'post_image_featured_remove', false)
      this.$set(this, 'previewImage', resource.file_url)
      this.$set(this, 'preview_image_resource_id', resource.id)
    },

    assignResource(resource) {
      /**
       * UNLIKE NORMAL TENANT POSTS WE NEED TO SWAP OUT THE ID WITH THE SOURCE_ID
       * This is becuase the resource has been sourced from the cloud and not the local resource model.
       * The cloud resource model has reference to the local resource model in it's source_id column!
       */
      if (!resource.source_id) {
        resource.source_id = resource.id
      } else {
        resource.id = resource.source_id
      }

      let exists = false
      this.post_resources.forEach((i) => {
        if (i.source_id === resource.source_id) {
          exists = true
        }
      })

      if (!exists) {
        let arr = []
        for (let i = 0; i < this.post_resources.length; i++) {
          arr.push(this.post_resources[i])
        }
        arr.push(resource)
        this.$set(this, 'post_resources', [])
        this.$set(this, 'post_resources', arr)
      }

      this.$set(this, 'showResourcesSearchModal', false)
    },

    getAndSetCurrentPostData() {
      this.$set(this, 'post_category_original', this.currentPost.category)
      this.$set(this, 'post_category', this.currentPost.category)

      if (this.currentPost.category === 3) {
        this.$set(
          this,
          'post_event_date_range_original',
          this.currentPost.event_date_range,
        )
        this.$set(
          this,
          'post_event_date_range',
          this.currentPost.event_date_range,
        )

        this.$set(
          this,
          'post_event_start_at_time_original',
          this.currentPost.event_start_at_time,
        )
        this.$set(
          this,
          'post_event_start_at_time',
          this.currentPost.event_start_at_time,
        )

        this.$set(
          this,
          'post_event_venue_title_original',
          this.currentPost.event_venue_title,
        )
        this.$set(
          this,
          'post_event_venue_title',
          this.currentPost.event_venue_title,
        )

        this.$set(
          this,
          'post_event_venue_address_original',
          this.currentPost.event_venue_address,
        )
        this.$set(
          this,
          'post_event_venue_address',
          this.currentPost.event_venue_address,
        )
      }
      if (this.currentPost.category === 2) {
        this.$set(this, 'post_job_type_original', this.currentPost.job_type)
        this.$set(this, 'post_job_type', this.currentPost.job_type)

        this.$set(
          this,
          'post_job_employer_original',
          this.currentPost.job_employer,
        )
        this.$set(this, 'post_job_employer', this.currentPost.job_employer)
      }

      this.$set(this, 'post_title_original', this.currentPost.title)
      this.$set(this, 'post_title', this.currentPost.title)

      this.$set(this, 'post_description_original', this.currentPost.description)
      this.$set(this, 'post_description', this.currentPost.description)

      this.$set(this, 'post_body_original', this.currentPost.body)
      this.$set(this, 'post_body', this.currentPost.body)

      // Save current selected tag ids
      this.$set(this, 'all_tags_selected', this.currentPost.tag_ids)

      // General tags
      let general_tags = []
      this.settings.tags.forEach((i) => {
        if (this.currentPost.tag_titles?.includes(i.text)) {
          general_tags.push(i.value)
        }
      })
      this.$set(this, 'general_tags_selected', general_tags)

      let industry_tags = []
      this.settings.tag_industries.forEach((i) => {
        if (this.currentPost.tag_titles?.includes(i.text)) {
          industry_tags.push(i.value)
        }
      })
      this.$set(this, 'industry_tags_selected', industry_tags)

      // Year tag
      let year_tags = []
      this.settings.tag_years.forEach((i) => {
        if (this.currentPost.tag_titles?.includes(i.text)) {
          year_tags.push(i.value)
        }
      })
      this.$set(this, 'year_tags_selected', year_tags)

      // Region tag
      let region_tag = null
      this.settings.tag_regions.forEach((obj) => {
        if (this.currentPost.tag_titles?.includes(obj.text)) {
          region_tag = obj.value
        }
      })
      this.$set(this, 'region_tag_selected', region_tag)

      this.$set(this, 'post_link_title_original', this.currentPost.link_title)
      this.$set(this, 'post_link_title', this.currentPost.link_title)

      this.$set(this, 'post_link_url_original', this.currentPost.link_url)
      this.$set(this, 'post_link_url', this.currentPost.link_url)

      this.$set(this, 'cloud_post_original', this.currentPost.cloud_post)
      this.$set(this, 'cloud_post', this.currentPost.cloud_post)

      this.$set(
        this,
        'post_image_featured_original',
        this.currentPost.image_featured,
      )
      this.$set(this, 'post_image_featured', this.currentPost.image_featured)
      this.$set(this, 'previewImage', this.currentPost.image_featured)
      this.$set(this, 'post_image_featured_remove', false)

      this.$set(this, 'post_resources_original', this.currentPost.resources)
      this.$set(this, 'post_resources', this.currentPost.resources)

      this.$set(this, 'publish_status', true)

      this.$set(this, 'post_status_original', this.currentPost.status)
      this.$set(this, 'post_status', 1)

      this.$set(this, 'data_loaded', true) // Set to loaded on first load...
    },

    updatePostCategory(val) {
      this.$set(this, 'post_category', val)
      this.$set(this, 'ddCategoryActive', false)
    },

    updateJobType(val) {
      this.$set(this, 'post_job_type', val)
      this.$set(this, 'ddJobTypeActive', false)
    },

    // IMAGE METHODS:

    selectImage() {
      this.$refs.selectImage.click()
    },

    pickImage() {
      let file = this.$refs.selectImage.files
      if (file && file[0]) {
        if (!this.validateImageFormat(file[0])) {
          return
        }
      }

      let reader = new FileReader()
      this.$set(this, 'post_image_featured', file[0])
      reader.onload = (e) => {
        this.$set(this, 'previewImageChanged', true)
        this.$set(this, 'post_image_featured_remove', false)
        this.previewImage = e.target.result
      }

      reader.readAsDataURL(file[0])
      this.$emit('input', file[0])
    },

    resetImage() {
      this.$set(this, 'previewImageChanged', false)
      this.$set(this, 'post_image_featured', null)
      this.$set(this, 'previewImage', this.post_image_featured_original)
      this.$set(this, 'preview_image_resource_id', null)
      this.$refs.selectImageForm.reset()
    },

    processUpdate() {
      let canSubmit = this.update.length > 0 ? true : false
      this.mandatory_options.forEach((i) => {
        const val = this.returnDataNodeValue(i)
        if (val === null) {
          canSubmit = false
        }
      })
      if (
        this.general_tags_selected.length + this.industry_tags_selected.length <
        1
      ) {
        canSubmit = false
      }

      this.$set(this, 'canSubmit', canSubmit)
    },

    returnDataNodeValue(key) {
      let value = null
      switch (key) {
        case 'status':
          value = this.post_status
          break
        case 'category':
          value = this.post_category
          break
        case 'title':
          value = this.post_title
          break
        case 'body':
          value = this.post_body
          break
        case 'description':
          value = this.post_description
          break
        case 'link_title':
          value = this.post_link_title
          break
        case 'link_url':
          value = this.post_link_url
          break
        case 'cloud_post':
          value = this.cloud_post
          break
        case 'event_date_range':
          value = this.post_event_date_range
          break
        case 'event_start_at_time':
          value = this.post_event_start_at_time
          break
        case 'event_venue_title':
          value = this.post_event_venue_title
          break
        case 'event_venue_address':
          value = this.post_event_venue_address
          break
        case 'event_start_at':
          value = this.post_event_start_at
          break
        case 'event_end_at':
          value = this.post_event_end_at
          break
        case 'job_type':
          value = this.post_job_type
          break
        case 'job_employer':
          value = this.post_job_employer
          break
        case 'image_featured':
          // value = this.post_image_featured;
          value = this.preview_image_resource_id
          break
        case 'image_remove':
          value = this.post_image_featured_remove
          break
        case 'resources_order': {
          /**
           *  arr.push(i.source_id);
           * UNLIKE NORMAL TENANT POSTS WE NEED TO SWAP OUT THE ID WITH THE SOURCE_ID
           * This is becuase the resource has been sourced from the cloud and not the local resource model.
           * The cloud resource model has reference to the local resource model in it's source_id column!
           */

          let arr = []
          this.post_resources.forEach((i) => {
            // arr.push(i.id);
            arr.push(i.source_id)
          })
          if (arr.length > 0) {
            value = arr
          } else {
            value = 0
          }

          break
        }

        case 'tags':
          value = this.all_tags_selected
          break

        case 'region_tag_selected': {
          value = this.region_tag_selected
          break
        }

        default:
          break
      }
      return value
    },

    deletePost() {
      if (!this.post_id || !this.userAccess) {
        return
      }

      this.thinkingDelete = true

      axios.get('/api/auth/delete-post/' + this.post_id).then((response) => {
        EventBus.$emit('deleteResponseReturned')
        if (response.data.success) {
          this.$router.push({ path: '/posts' })
        }
        if (response.data.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }
        EventBus.$emit('closeOtherPostsManagerMenus')
        if (response.data.settings?.website) {
          EventBus.$emit('reloadWebsiteSettings')
        }
      })
    },

    // SAVE API

    submitUpdates() {
      if (!this.canSubmit) {
        return
      }

      let submitData = new FormData()
      let score = 0
      Object.entries(this.update).map(([, value]) => {
        let skip = false
        if (value === 'description') {
          if (this.post_description.length < 1) {
            submitData.append('description_clear', true)
            skip = true
          }
        }
        if (value === 'body') {
          if (this.post_body.length < 1) {
            submitData.append('body_clear', true)
            skip = true
          }
        }

        if (value === 'link_title') {
          if (this.post_link_title.length < 1) {
            submitData.append('link_clear', true)
            skip = true
          }
        }
        if (value === 'link_url') {
          if (this.post_link_url.length < 1) {
            submitData.append('link_clear', true)
            skip = true
          }
        }
        if (value === 'job_employer') {
          if (this.post_job_employer.length < 1) {
            submitData.append('job_employer_clear', true)
            skip = true
          }
        }
        if (value === 'event_venue_title') {
          if (this.post_event_venue_title.length < 1) {
            submitData.append('event_venue_title_clear', true)
            skip = true
          }
        }
        if (value === 'event_venue_address') {
          if (this.post_event_venue_address.length < 1) {
            submitData.append('event_venue_address_clear', true)
            skip = true
          }
        }
        if (value === 'tags') {
          if (this.all_tags_selected.length < 1) {
            submitData.append('tags_clear', true)
            skip = true
          }
        }

        if (!skip) {
          let val = this.returnDataNodeValue(value)

          if (
            (value === 'post_link_url' || value === 'post_link_title') &&
            val.length < 1
          ) {
            switch (value) {
              case 'post_link_url':
                value = 'remove_link_url'
                val = true
                break

              case 'post_link_title':
                value = 'remove_link_title'
                val = true
                break

              default:
                break
            }
          }

          if (val !== null || val !== '') {
            score = score + 1
          }
          if (val !== null || val === '') {
            submitData.append(`${value}`, val)
          }
        } else {
          score = score + 1
        }
      })

      if (score < 1) {
        return
      }

      submitData.append('post_id', this.currentPost.id)

      this.$set(this, 'canSubmit', false)
      this.$set(this, 'thinking', true)

      axios
        .post('/api/auth/update-post-with-relic', submitData)
        .then((response) => {
          if (response.data.success) {
            if (response.data.post) {
              this.dispatchSetCurrentPost(response.data.post)
            }

            // this.resetPostData;
            this.$set(this, 'update', [])
            this.$set(this, 'thinking', false)
            EventBus.$emit('update_post_success', this.post_id)
          } else {
            this.$set(this, 'thinking', false)
          }

          if (response.data.settings?.website) {
            EventBus.$emit('reloadWebsiteSettings')
          }

          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
          }
        })
    },

    resetPostData() {
      EventBus.$emit('get_new_post_data', this.currentPost.id)
    },
  },
}
</script>

<style scoped>
.post-intro::after {
  content: '';
  background: #fff;
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}

.banner-changer::after {
  background: #000;
  opacity: 0.6;
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 0.25rem;
}

.dj-datepicker .mx-datepicker {
  width: 100%;
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
    rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;
}
.dj-datepicker .mx-datepicker .mx-input {
  height: 3rem;
  width: 100%;
}
</style>
